import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@/components/tables/Table';
import { Car, CarWithLoadedModel } from '../api/carsApi';
import { Link, useSearchParams } from 'react-router-dom';
import { ModalWithSearchParam } from '@/components/ui/modal/ModalWithSearchParam';
import { useModalSearchParam } from '@/shared/hooks/useModalSearchParam';
import CarEditForm from './CarEditForm';
import { useUpdateCar, useDeleteCar } from '../hooks/useCars';
import Badge from '@/components/ui/badge/Badge';
import { CarStatsCell } from './CarStatsCell';
import { useCarStats } from '../hooks/useCarStats';
import { StatsPeriod } from '../api/carStatsApi';
import Button from '@/components/ui/button/Button';
import { useToast } from '@/components/ui/toast/Toast';
import { useTableColumns } from '@/shared/hooks/useTableColumns';

interface CarsTableProps {
  cars: CarWithLoadedModel[];
  isLoading: boolean;
  initialEditCar?: CarWithLoadedModel | null;
}

export const CarsTable: React.FC<CarsTableProps> = ({ cars, isLoading, initialEditCar }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [selectedCar, setSelectedCar] = useState<CarWithLoadedModel | null>(null);
  const updateCar = useUpdateCar();
  const deleteCar = useDeleteCar();
  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>('month');
  const { data: stats, isLoading: statsLoading } = useCarStats(statsPeriod);
  const { visibleColumns } = useTableColumns();
  const formRef = useRef<{ handleClose: () => void; isDirty: () => boolean }>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const isDevMode = import.meta.env.DEV;

  const { isOpen, open: openModal, close } = useModalSearchParam({
    paramName: 'edit'
  });

  useEffect(() => {
    const editCarId = searchParams.get('edit');
    if (editCarId && cars.length > 0) {
      const carToEdit = cars.find(car => car.id === editCarId) || initialEditCar;
      if (carToEdit && (!selectedCar || selectedCar.id !== carToEdit.id)) {
        setSelectedCar(carToEdit);
      }
    } else if (!editCarId && selectedCar) {
      setSelectedCar(null);
    }
  }, [searchParams, cars, initialEditCar, selectedCar]);

  const handleModalClose = useCallback(() => {
    const hasUnsavedChanges = formRef.current?.isDirty() || false;
    if (hasUnsavedChanges) {
      formRef.current?.handleClose();
    } else {
      setSearchParams(params => {
        params.delete('edit');
        return params;
      });
      setSelectedCar(null);
    }
  }, [setSearchParams]);

  const handleFormClose = useCallback((hasUnsavedChanges?: boolean) => {
    if (!hasUnsavedChanges) {
      setSearchParams(params => {
        params.delete('edit');
        return params;
      });
      setSelectedCar(null);
    }
  }, [setSearchParams]);

  const handleEdit = (car: CarWithLoadedModel) => {
    setSearchParams(params => {
      params.set('edit', car.id);
      return params;
    });
  };

  const handleDeleteClick = async (carId: string) => {
    try {
      await deleteCar.mutateAsync(carId);
      toast.success(t('cars.delete.success'));
    } catch (error) {
      toast.error(t('cars.delete.error'));
    }
  };

  const handleUpdateCar = async (data: Partial<Car>) => {
    if (!selectedCar) return;

    try {
      await updateCar.mutateAsync({ id: selectedCar.id, data });
      setSearchParams(params => {
        params.delete('edit');
        return params;
      });
      setSelectedCar(null);
    } catch (error) {
      throw error;
    }
  };

  const columns = [
    {
      key: 'image',
      header: t('cars.columns.image'),
      field: 'image',
      headerClassName: 'w-[200px]',
      className: 'w-[200px]',
      cell: (row: CarWithLoadedModel) => (
        <div className="relative" key={`image-${row.id}`}>
          <Link
            to={`/cars/${row.id}`}
            className="block relative"
            title={t('cars.view_car_details')}
          >
            <img
              src={row.image_url}
              alt={`${row.model.brand_name} ${row.model.model_name}`}
              className="w-full rounded-lg object-cover flex-shrink-0 border border-border-primary outline-[0px] transition-all duration-100 hover:outline hover:outline-[2px] outline-brand-400"
            />
          </Link>
        </div>
      ),
    },
    {
      key: 'model',
      header: t('cars.columns.info'),
      field: 'model',
      cell: (row: CarWithLoadedModel) => (
        <div className="flex flex-col">
          <Link
            to={`/models/${row.model.id}`}
            className="font-medium hover:text-brand-500 transition-colors text-text-primary"
            title={t('cars.view_model_details')}
          >
            {row.model.brand_name} {row.model.model_name}
          </Link>
          <span className="text-sm text-text-tertiary">
            {row.registration_number}
          </span>
          <div className="flex gap-2 mt-1">
            <Badge size="sm" color="primary">
              {row.model.body_type.toUpperCase()}
            </Badge>
            <Badge size="sm" color="info">
              {row.model.transmission_type.toUpperCase()}
            </Badge>
          </div>
        </div>
      ),
    },
    {
      key: 'color',
      header: t('cars.columns.color'),
      field: 'color',
      cell: (row: CarWithLoadedModel) => (
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded-full border border-border-primary"
            style={{ backgroundColor: row.color }}
          />
          <span className="capitalize text-text-primary">{row.color}</span>
        </div>
      ),
    },
    {
      key: 'price',
      header: t('cars.columns.price'),
      field: 'price',
      cell: (row: CarWithLoadedModel) => (
        <span className="font-medium text-text-primary">
          {row.price}
        </span>
      ),
    },
    {
      key: 'stats',
      header: t('cars.columns.usage_stats'),
      field: 'stats',
      cell: (row: CarWithLoadedModel) => (
        <CarStatsCell
          carId={row.id}
          mode="compact"
        />
      ),
    },
    {
      key: 'actions',
      header: t('cars.columns.actions'),
      field: 'actions',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (row: CarWithLoadedModel) => (
        <div className="flex gap-2 justify-end" key={`actions-${row.id}`}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleEdit(row)}
          >
            {t('cars.actions.edit')}
          </Button>
          <Link to={`/cars/${row.id}`}>
            <Button
              size="sm"
              variant="primary"
            >
              {t('cars.actions.details')}
            </Button>
          </Link>
          {isDevMode && (
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleDeleteClick(row.id)}
            >
              {t('Delete')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const filteredColumns = useMemo(() => {
    if (visibleColumns && visibleColumns.length > 0) {
      return columns.filter(col => visibleColumns.includes(col.key));
    }
    return columns;
  }, [columns, visibleColumns]);

  return (
    <>
      <Table<CarWithLoadedModel>
        data={cars}
        columns={filteredColumns}
        loading={isLoading}
      />

      <ModalWithSearchParam
        paramName="edit"
        onClose={handleModalClose}
        className="p-6 lg:p-10"
        maxWidth="1500px"
      >
        {selectedCar && (
          <CarEditForm
            ref={formRef}
            car={selectedCar}
            onSubmit={handleUpdateCar}
            onClose={handleFormClose}
            initialData={selectedCar}
          />
        )}
      </ModalWithSearchParam>
    </>
  );
};
